/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */
import Jsona from 'jsona';

import * as API from '../utils/api';
import Types from '../constants/actionTypes';
import modalTypes from '../constants/modalTypes';
import emailList from '../constants/emailList';
import Store from '../store/store';
import { applicationError, notificationAction } from './notificationActions';
import { fetchUsers } from './userActions';
import { fetchTeams } from './teamActions';
import { openModal } from './modalActions';
import { resolveUser } from '../utils/reduxHelpers';

export function updateOrganizationStore(
  organization,
  action,
  dispatchingUserId
) {
  return (dispatch) => {
    switch (action) {
      case 'update':
        const currentUser = Store.getState().userReducer.user;
        if (currentUser.id === dispatchingUserId) {
          dispatch({
            type: Types.UPDATE_ORGANIZATION,
            organization
          });
        } else {
          dispatch(getOrganizations());
        }
        dispatch(fetchTeams());
        dispatch(fetchUsers(true));
        return;
      default:
        return null;
    }
  };
}

const dataFormatter = new Jsona();
const cadenceId = () => Store.getState().cadenceReducer.cadence?.id;

export function getOrganizations() {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_ORGANIZATIONS });
    return API.get('organizations')
      .then((response) => {
        const organizations = dataFormatter.deserialize(response);

        dispatch({
          type: Types.RECEIVE_ORGANIZATIONS,
          organizations
        });
      })
      .catch((error) => {
        dispatch(applicationError(error));
        dispatch({ type: Types.REQUEST_ORGANIZATIONS_FAILED });
      });
  };
}

export function setTarget(id) {
  return (dispatch) => dispatch({ type: Types.SET_TARGET, target: id });
}

export const createUser = (organizationId, data) => (dispatch) => {
  dispatch({ type: Types.CREATE_USER });
  return API.post(`organizations/${organizationId}/create_user`, data)
    .then((response) => response.json())
    .then((response) => {
      const user = dataFormatter.deserialize(response);
      resolveUser(user);
      // handle uploaded image
      if (data.user && data.user.image && data.user.image.droppedFile) {
        const uploadData = new FormData();
        const fileCopy = data.user.image.droppedFile;
        fileCopy.path = data.user.image.droppedFileUrl;
        uploadData.append('user[image]', fileCopy);
        uploadData.append('user[id]', user.id);
        dispatch({ type: Types.UPLOAD_PROFILE_PICTURE });
        return API.filePost(
          `users/${user.id}/upload_picture`,
          uploadData,
          'PUT'
        )
          .then((uploadResponse) => uploadResponse.json())
          .then(() => {
            dispatch(
              notificationAction(
                `${user.name} has been added to the organization`,
                2
              )
            );
            dispatch({ type: Types.USER_NOT_FETCHING });
            user.teams.forEach((team) => {
              dispatch({ type: Types.ADD_MEMBER, user, team });
            });
            dispatch({
              type: Types.RECEIVE_ORGANIZATION_USER,
              user,
              organizationId
            });
            dispatch({ type: Types.CLOSE_MODAL, id: 'user' });
          })
          .catch((error) => {
            dispatch(applicationError(error));
          });
      }
      dispatch({
        type: Types.ADD_USER,
        user
      });
      dispatch(
        notificationAction(`${user.name} has been added to the organization`, 2)
      );
      dispatch({ type: Types.USER_NOT_FETCHING });
      if (user.teams.length > 0) {
        user.teams.forEach((team) => {
          dispatch({ type: Types.ADD_MEMBER, user, team });
        });
      }

      dispatch({
        type: Types.RECEIVE_ORGANIZATION_USER,
        user,
        organizationId
      });
      return dispatch({ type: Types.CLOSE_MODAL, id: 'user' });
    })
    .catch((error) => {
      dispatch({ type: Types.UPDATE_USER_ERROR, formError: error?.errors });
    });
};

export function updateOrganization(organizationId, data) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_ORGANIZATION_UPDATE });
    return API.put(`organizations/${organizationId}`, data)
      .then((response) => response.json().then((body) => body))
      .then((response) => {
        const organization = dataFormatter.deserialize(response);
        dispatch({
          type: Types.UPDATE_ORGANIZATION,
          organization
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function updateOrganizationLevel(organizationId, organizationLevel) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_ORGANIZATION_UPDATE });
    return API.put(
      `organizations/${organizationId}/organization-levels/${organizationLevel.id}`,
      { organization_level: organizationLevel }
    )
      .then((response) => response.json().then((body) => body))
      .then((response) => {
        const organization = dataFormatter.deserialize(response);
        dispatch({
          type: Types.UPDATE_ORGANIZATION,
          organization
        });
        dispatch({
          type: Types.UPDATE_WORK_PACKAGE_KIND,
          organizationLevel,
          cadenceId: cadenceId()
        });
      })
      .catch((error) => {
        dispatch({ type: Types.CANCEL_ORGANIZATION_FETCHING });
        return dispatch(applicationError(error));
      });
  };
}

export function changeDefaultOrganizationLevel(
  prevDefaultID = null,
  newDefaultID,
  organizationID
) {
  return (dispatch) =>
    API.put('organizations/update_default_org_level', {
      level_ids: {
        previous_default_id: prevDefaultID,
        new_default_id: newDefaultID
      },
      id: organizationID
    })
      .then((response) => response.json().then((body) => body))
      .then((data) => {
        dispatch({
          type: Types.UPDATE_DEFAULT_ORG_LEVEL,
          payload: { prevDefaultID, newDefaultID, organizationID }
        });
      })
      .catch((error) => dispatch(applicationError(error)));
}

export function deleteOrganizationLevel(organization, organizationLevel) {
  return (dispatch) => {
    // User can just delete organization levels if more than 1 will be left for that level
    if (
      organization.organization_levels.filter(
        (orgLevel) => orgLevel.level === organizationLevel.level
      ).length > 1
    ) {
      const workPackages = Store.getState().workPackageReducer.all;
      if (
        workPackages.find(
          (wp) =>
            wp.result_type &&
            wp.result_type.organization_level &&
            wp.result_type.organization_level.id === organizationLevel.id
        )
      ) {
        return dispatch(
          applicationError(
            'Remove all work packages from this level before delete it'
          )
        );
      }
      dispatch({ type: Types.REQUEST_ORGANIZATION_UPDATE });
      return API.del(
        `organizations/${organization.id}/organization-levels/${organizationLevel.id}`
      )
        .then((response) => response.json().then((body) => body))
        .then((response) => {
          const org = dataFormatter.deserialize(response);
          dispatch({
            type: Types.UPDATE_ORGANIZATION,
            organization: org
          });
        })
        .catch((error) => {
          dispatch({ type: Types.CANCEL_ORGANIZATION_FETCHING });
          return dispatch(applicationError(error));
        });
    }
    return dispatch(applicationError('Level 1 has to have at least one item'));
  };
}

export const addOrgRole = (orgId, userId, roleType) => (dispatch) => {
  dispatch({ type: Types.REQUEST_ORGANIZATION_UPDATE });
  return API.put(`organizations/${orgId}/add_role`, {
    user_id: userId,
    role_type: roleType
  })
    .then((response) => response.json().then((body) => body))
    .then((response) => {
      const user = dataFormatter.deserialize(response);
      const currentUser = Store.getState().userReducer.user || {};
      if (currentUser.id === userId) {
        dispatch({ type: Types.UPDATE_USER_ROLES, user });
      } else {
        dispatch({ type: Types.UPDATE_USER, user });
      }
      dispatch({ type: Types.CANCEL_ORGANIZATION_FETCHING });
    })
    .catch((error) => {
      dispatch(applicationError(error));
      dispatch({ type: Types.CANCEL_ORGANIZATION_FETCHING });
    });
};

export const removeOrgRole = (orgId, roleId, userId) => (dispatch) => {
  dispatch({ type: Types.REQUEST_ORGANIZATION_UPDATE });
  return API.put(`organizations/${orgId}/remove_role`, {
    role_id: roleId,
    user_id: userId
  })
    .then((response) => response.json().then((body) => body))
    .then((response) => {
      const user = dataFormatter.deserialize(response);
      const currentUser = Store.getState().userReducer.user || {};
      if (currentUser.id === userId) {
        dispatch({ type: Types.UPDATE_USER_ROLES, user });
      } else {
        dispatch({ type: Types.UPDATE_USER, user });
      }
      dispatch({ type: Types.CANCEL_ORGANIZATION_FETCHING });
    })
    .catch((error) => {
      dispatch(applicationError(error));
      dispatch({ type: Types.CANCEL_ORGANIZATION_FETCHING });
    });
};

export function restoreOrganizationLevel(organization, organizationLevel) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_ORGANIZATION_UPDATE });
    return API.put(
      `organizations/${organization.id}/organization-levels/${organizationLevel.id}/restore`
    )
      .then((response) => response.json().then((body) => body))
      .then((response) => {
        const org = dataFormatter.deserialize(response);
        dispatch({
          type: Types.UPDATE_ORGANIZATION,
          organization: org
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function createOrganizationLevel(organizationId, organizationLevel) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_ORGANIZATION_UPDATE });
    return API.post(`organizations/${organizationId}/organization-levels/`, {
      organization_level: organizationLevel
    })
      .then((response) => response.json().then((body) => body))
      .then((response) => {
        const organization = dataFormatter.deserialize(response);
        dispatch({
          type: Types.UPDATE_ORGANIZATION,
          organization
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function uploadOrganizationImage(file, fileUrl, organizationId) {
  const data = new FormData();
  const fileCopy = file;
  fileCopy.path = fileUrl;
  data.append('organization[image]', fileCopy);
  data.append('organization[id]', organizationId);
  return (dispatch) => {
    dispatch({ type: Types.UPLOAD_ORGANIZATION_PICTURE });
    return API.filePost(
      `organizations/${organizationId}/upload_picture`,
      data,
      'PUT'
    )
      .then((response) => response.json())
      .then((response) => {
        const organization = dataFormatter.deserialize(response);
        dispatch({
          type: Types.UPDATE_ORGANIZATION,
          organization
        });
      })
      .catch((error) => {
        dispatch(applicationError(error));
      });
  };
}

export function deleteUser(orgId, userId) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_USER_UPDATE });
    return API.put(`organizations/${orgId}/delete_user`, { user_id: userId })
      .then((response) => response.json())
      .then((response) => {
        const organization = dataFormatter.deserialize(response);
        dispatch({
          type: Types.UPDATE_ORGANIZATION,
          organization
        });
        dispatch(fetchUsers(true));
      })
      .catch((error) => {
        dispatch(applicationError(error));
      });
  };
}

export function deleteUserPermanently(orgId, userId) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_USER_UPDATE });
    return API.put(`organizations/${orgId}/delete_user_permanently`, {
      user_id: userId
    })
      .then((response) => response.json())
      .then((response) => {
        const organization = dataFormatter.deserialize(response);
        dispatch({
          type: Types.UPDATE_ORGANIZATION,
          organization
        });
        dispatch(fetchUsers(true));
      })
      .catch((error) => {
        dispatch(applicationError(error));
      });
  };
}

export function restoreUser(orgId, userId) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_USER_UPDATE });
    return API.put(`organizations/${orgId}/restore_user`, { user_id: userId })
      .then((response) => response.json())
      .then((response) => {
        const organization = dataFormatter.deserialize(response);
        dispatch({
          type: Types.UPDATE_ORGANIZATION,
          organization
        });
        dispatch(fetchUsers(true));
      })
      .catch((error) => {
        dispatch(applicationError(error));
      });
  };
}

export const userLimitModal = (memberLimit) => (dispatch) => {
  const actions = {
    Upgrade: {
      name: 'Upgrade',
      function: () =>
        (window.location.href = `mailto:${emailList.upgrade}?subject=Upgrade Subscription!`)
    }
  };

  dispatch(
    openModal(modalTypes.message, {
      message: `Your subscription only allows ${memberLimit} active users. Would you like to upgrade?`,
      closeButtonTitle: 'Close',
      actions
    })
  );
};
